import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { ArrowLeft, FileText, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { EditCustomerDialog } from "@/components/EditCustomerDialog";
import { CustomerWhatsAppCard } from "@/components/CustomerWhatsApp";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { CreditNotesTable } from "@/components/CreditNotesTable";
import { formatCurrency, formatDate } from "@/lib/format";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  EmptyState,
  ErrorState,
  Eyebrow,
  HeaderRow,
  LoadingState,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";

type CustomerResponse = components["schemas"]["CustomerResponse"];
type InvoiceResponse = components["schemas"]["InvoiceResponse"];
type CreditNoteResponse = components["schemas"]["CreditNoteResponse"];

export interface CustomerPageProps {
  customerId: string;
  /** Called when the user clicks the back affordance. Falls back to history.back() if neither this nor LinkComponent is provided. */
  onBack?: () => void;
  /** Called after the customer is deleted. Falls back to onBack, then history.back(). */
  onDeleted?: () => void;
  /** Renders the back link as a routed anchor (e.g. next/link, react-router Link). Receives `href="/customers"`. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  /** Path passed to LinkComponent if provided. Defaults to "/customers". */
  backHref?: string;
  onInvoiceClick?: (invoiceId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
  /** Passed in: the component can't see the host's entitlements. Absent, the e-invoicing panel doesn't appear. */
  eInvoicingEnabled?: boolean;
  /** Whether the client holds the WhatsApp module. Absent, the WhatsApp card does not appear. */
  whatsAppEnabled?: boolean;
}

export function CustomerPage({
  customerId,
  onBack,
  onDeleted,
  LinkComponent,
  backHref = "/customers",
  onInvoiceClick,
  onCreditNoteClick,
  eInvoicingEnabled,
  whatsAppEnabled,
}: CustomerPageProps) {
  const api = useDolfinClient();
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [creditNotes, setCreditNotes] = useState<CreditNoteResponse[]>([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Everything this customer still owes, fetched separately from the recent-invoices list so the
  // balance is not limited to the ten rows shown below.
  const [openInvoices, setOpenInvoices] = useState<InvoiceResponse[] | null>(null);
  const [openInvoicesTruncated, setOpenInvoicesTruncated] = useState(false);

  async function fetchCustomer() {
    if (!customerId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await api.GET("/v1/customers/{id}", {
        params: { path: { id: customerId! } },
      });

      if (apiError || !data) {
        console.error("Failed to load customer:", apiError);
        setError("Failed to load customer");
        return;
      }

      setCustomer(data);
    } catch (e) {
      console.error("Failed to load customer:", e);
      setError("Failed to load customer");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Credit notes filter by customer through the invoice nav server-side.
  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      setInvoicesLoading(true);
      setCreditNotesLoading(true);
      const [inv, cn, open] = await Promise.all([
        api.GET("/v1/invoices", { params: { query: { CustomerId: customerId, Limit: 10 } } }),
        api.GET("/v1/credit-notes", { params: { query: { CustomerId: customerId, Limit: 10 } } }),
        api.GET("/v1/invoices", {
          params: { query: { CustomerId: customerId, States: ["Issued", "PartiallyPaid"], Limit: 100 } },
        }),
      ]);
      if (cancelled) return;
      setOpenInvoices(open.data?.data ?? null);
      setOpenInvoicesTruncated(!!open.data?.meta?.cursors?.next);
      setInvoices(inv.data?.data ?? []);
      setInvoicesLoading(false);
      setCreditNotes(cn.data?.data ?? []);
      setCreditNotesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, customerId]);

  async function handleDelete() {
    if (!customerId || deleting) return;
    setConfirmingDelete(false);
    setDeleting(true);

    const { error: apiError } = await api.DELETE("/v1/customers/{id}", {
      params: { path: { id: customerId } },
    });

    if (apiError) {
      const message = apiErrorMessage(
        apiError,
        "Failed to delete customer. It may have associated invoices.",
      );
      toast.error(message);
      setDeleting(false);
      return;
    }

    (onDeleted ?? onBack ?? (() => window.history.back()))();
  }

  if (loading && !customer) {
    return (
      <PageContainer>
        <LoadingState label="Loading customer..." />
      </PageContainer>
    );
  }

  if (error || !customer) {
    return (
      <PageContainer>
        <BackAffordance onBack={onBack} LinkComponent={LinkComponent} backHref={backHref} />
        <ErrorState message={error ? "Couldn't load this customer." : "That customer could not be found."} onRetry={error ? fetchCustomer : undefined} />
      </PageContainer>
    );
  }

  const address = [
    customer.addressLine1,
    customer.addressLine2,
    customer.city,
    customer.state,
    customer.postalCode,
    customer.country,
  ].filter(Boolean);

  return (
    <PageContainer>
      <BackAffordance
        onBack={onBack}
        LinkComponent={LinkComponent}
        backHref={backHref}
      />

      <PageHeader
        eyebrow="Customer"
        title={customer.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {[customer.email, customer.phone, [customer.city, customer.country].filter(Boolean).join(", ")]
              .filter(Boolean)
              .map((part, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 ? <span aria-hidden>·</span> : null}
                  {part}
                </span>
              ))}
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" size="sm" disabled={deleting} onClick={() => setConfirmingDelete(true)}>
              {deleting ? (
                <Spinner className="size-4 mr-1" />
              ) : (
                <Trash2 className="size-4 mr-1" />
              )}
              Delete
            </Button>
          </>
        }
      />

      {openInvoices ? <BalanceStrip invoices={openInvoices} truncated={openInvoicesTruncated} /> : null}

      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
        <DetailCard title="Contact">
          <DetailRow label="Email" value={customer.email} />
          <DetailRow label="Phone" value={customer.phone} />
          <DetailRow label="Tax number" value={customer.taxNumber} />
        </DetailCard>

        {address.length > 0 && (
          <DetailCard title="Address">
            <DetailRow label="Line 1" value={customer.addressLine1} />
            <DetailRow label="Line 2" value={customer.addressLine2} />
            <DetailRow label="City" value={customer.city} />
            <DetailRow label="State" value={customer.state} />
            <DetailRow label="Postal code" value={customer.postalCode} />
            <DetailRow label="Country" value={customer.country} />
          </DetailCard>
        )}

        {eInvoicingEnabled && (
          <DetailCard title="E-invoicing">
            {customer.canReceiveEInvoices ? (
              <>
                <DetailRow label="Network" value={networkLabel(customer.electronicAddressScheme)} />
                <DetailRow label="Address type" value={schemeLabel(customer.electronicAddressScheme)} />
                <DetailRow label="Address" value={customer.electronicAddress} mono />
              </>
            ) : (
              // Careful not to overclaim: a customer outside Italy is sent to over SDI without an address, so
              // "cannot be sent" would be false for them.
              <p className="text-sm text-muted-foreground">
                No e-invoicing address on file. Add one with Edit if this customer receives invoices
                over a network.
              </p>
            )}
          </DetailCard>
        )}

        {whatsAppEnabled && <CustomerWhatsAppCard customer={customer} onChange={setCustomer} />}

        <DetailCard title="Record">
          <DetailRow label="Customer ID" value={customer.id} mono />
          <DetailRow label="Created" value={formatDate(customer.createdAt, "long")} />
          <DetailRow label="Last updated" value={formatDate(customer.updatedAt, "long")} />
        </DetailCard>
      </div>

      <section className="mt-8 space-y-3">
        <SectionHeading title="Invoices" />
        <CustomerInvoicesTable
          invoices={invoices}
          loading={invoicesLoading}
          onInvoiceClick={onInvoiceClick}
        />
      </section>

      <section className="mt-8 space-y-3">
        <SectionHeading title="Credit notes" />
        <CreditNotesTable
          creditNotes={creditNotes}
          loading={creditNotesLoading}
          emptyMessage="No credit notes for this customer yet."
          onRowClick={(cn) =>
            onCreditNoteClick && cn.id ? onCreditNoteClick(cn.id) : cn.invoiceId && onInvoiceClick?.(cn.invoiceId)
          }
        />
      </section>

      <EditCustomerDialog
        customer={customer}
        open={editing}
        onOpenChange={setEditing}
        onUpdated={fetchCustomer}
      />

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {customer.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The customer will be permanently removed. This fails if they have any invoices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}

function BackAffordance({
  onBack,
  LinkComponent,
  backHref,
}: {
  onBack?: () => void;
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  backHref: string;
}) {
  const className =
    "mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground";
  const content = (
    <>
      <ArrowLeft className="size-3.5" />
      Back to customers
    </>
  );

  if (LinkComponent) {
    return (
      <LinkComponent href={backHref} className={className}>
        {content}
      </LinkComponent>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => (onBack ? onBack() : window.history.back())}
    >
      {content}
    </button>
  );
}

/** Which network the address belongs to. Italy's two schemes are both the same hub. */
function networkLabel(scheme: string | null | undefined) {
  if (!scheme) return null;
  return scheme === "Peppol" ? "Peppol" : "SDI (Italy)";
}

function schemeLabel(scheme: string | null | undefined) {
  switch (scheme) {
    case "Peppol":
      return "Participant id";
    case "ItalianSdiCode":
      return "Codice destinatario";
    case "ItalianSdiPec":
      return "PEC address";
    default:
      return null;
  }
}

function BalanceStrip({ invoices, truncated }: { invoices: InvoiceResponse[]; truncated: boolean }) {
  const today = new Date().toISOString().slice(0, 10);
  const byCurrency = new Map<string, { owed: number; overdue: number }>();
  let overdueCount = 0;
  for (const inv of invoices) {
    const cur = inv.currency ?? "";
    const owed = Number(inv.outstanding ?? inv.totalAmount ?? 0);
    const entry = byCurrency.get(cur) ?? { owed: 0, overdue: 0 };
    entry.owed += owed;
    if (inv.dueDate && inv.dueDate.slice(0, 10) < today) {
      entry.overdue += owed;
      overdueCount++;
    }
    byCurrency.set(cur, entry);
  }
  const rows = [...byCurrency.entries()];
  const prefix = truncated ? "At least " : "";

  return (
    <div className="mb-4 grid grid-cols-1 gap-4 @xl:grid-cols-2">
      <div className="rounded-xl border bg-card/60 p-4 backdrop-blur-sm">
        <Eyebrow>Owed to you</Eyebrow>
        {rows.length === 0 ? (
          <p className="mt-1 text-2xl font-bold tracking-tight">Nothing</p>
        ) : (
          rows.map(([cur, v]) => (
            <p key={cur} className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
              {prefix}
              {formatCurrency(v.owed, cur)}
            </p>
          ))
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          {invoices.length === 0
            ? "No unpaid invoices."
            : `Across ${prefix.toLowerCase()}${invoices.length} unpaid ${invoices.length === 1 ? "invoice" : "invoices"}.`}
        </p>
      </div>
      <div className="rounded-xl border bg-card/60 p-4 backdrop-blur-sm">
        <Eyebrow>Overdue</Eyebrow>
        {overdueCount === 0 ? (
          <p className="mt-1 text-2xl font-bold tracking-tight">Nothing</p>
        ) : (
          rows
            .filter(([, v]) => v.overdue > 0)
            .map(([cur, v]) => (
              <p key={cur} className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-red-600">
                {formatCurrency(v.overdue, cur)}
              </p>
            ))
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          {overdueCount === 0
            ? "Every unpaid invoice is within its terms."
            : `${overdueCount} ${overdueCount === 1 ? "invoice is" : "invoices are"} past due.`}
        </p>
      </div>
    </div>
  );
}

function DetailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <Eyebrow className="mb-3">{title}</Eyebrow>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right font-medium ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {title}
    </h2>
  );
}

/** "Due 3 Oct" in red once it has passed, so a late invoice reads as late without a separate column. */
function dueLine(inv: InvoiceResponse) {
  if (!inv.dueDate) return <span>Issued {formatDate(inv.issueDate)}</span>;
  const open = inv.status === "Issued" || inv.status === "PartiallyPaid";
  const late = open && inv.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);
  return <span className={cn(late && "text-red-600")}>{late ? "Overdue since" : "Due"} {formatDate(inv.dueDate)}</span>;
}

function InvoiceStatusBadge({ status }: { status: string | undefined }) {
  const tone =
    status === "Paid"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : status === "Issued"
        ? "border-accent-blue/30 bg-accent-blue/10 text-accent-blue"
        : status === "Void"
          ? "border-red-500/30 bg-red-500/10 text-red-700"
          : status === "PartiallyPaid"
            ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
            : status === "Credited"
              ? "border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-400"
              : "border-border bg-muted text-muted-foreground";
  return (
    <Badge variant="outline" className={tone}>
      {status === "PartiallyPaid" ? "Partial" : (status ?? "-")}
    </Badge>
  );
}

function CustomerInvoicesTable({
  invoices,
  loading,
  onInvoiceClick,
}: {
  invoices: InvoiceResponse[];
  loading?: boolean;
  onInvoiceClick?: (invoiceId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
}) {
  if (loading) {
    return <LoadingState label="Loading invoices..." />;
  }

  if (invoices.length === 0) {
    return (
      <EmptyState icon={FileText} title="No invoices yet">
        Invoices you raise for this customer will appear here.
      </EmptyState>
    );
  }

  return (
    <TableShell>
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Invoice</TableHead>
            <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
            <TableHead className={cn(TH, "w-[100px] text-right")}>Status</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => {
            const partlyOpen = inv.status === "PartiallyPaid" && inv.outstanding != null;
            return (
              <TableRow key={inv.id} {...rowLink(onInvoiceClick && inv.id ? () => onInvoiceClick(inv.id!) : undefined)}>
                <TableCell>
                  <div className="font-mono text-[13px] font-semibold">{inv.documentNumber || "Draft"}</div>
                  <div className="text-xs text-muted-foreground">{dueLine(inv)}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="font-semibold tabular-nums">{formatCurrency(inv.totalAmount, inv.currency)}</div>
                  {partlyOpen ? (
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {formatCurrency(inv.outstanding, inv.currency)} left to pay
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  <InvoiceStatusBadge status={inv.status ?? undefined} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableShell>
  );
}
